import { Box, Card, CardBody, CardHeader, Heading, Image, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { buildFlightPathString } from '@pelicargo/ui';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { CopyButton } from '../../components/CopyButton';
import { CopyLineItemsButton } from '../../components/CopyLineItemsButton';
import { PaperworkCard } from '../../components/PaperworkCard';
import { QuoteVariantUI } from '../../components/QuoteVariantTabs/QuoteVariant';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { Timeline } from '../../components/Timeline';
import { Validity } from '../../components/Validity';
import { TrpcOutputs } from '../../config/trpc';
import { useFlightData } from '../../hooks/useFlightData';

const schema = z.object({
  pickup_ref: z.string().nullish(),
});

type FormSchema = z.infer<typeof schema>;

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
};

export const AFRFPDashboard = ({ quote }: Props) => {
  const { flightData } = useFlightData(quote?.flight_path);

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;

  const methods = useForm<FormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      pickup_ref: bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref || '',
    },
  });

  useEffect(() => {
    const pickup_ref = bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref || '';
    if (pickup_ref) methods.setValue('pickup_ref', pickup_ref);
  }, [methods, bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref]);

  const stopCountLabel = useMemo(() => {
    const numStops = quote?.flight_path?.length - 1;

    if (numStops === 0) return 'Nonstop';
    if (numStops === 1) return '1 stop';
    return `${numStops} stops`;
  }, [quote?.flight_path?.length]);

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
      <Stack w="full" spacing="6">
        <Card borderRadius="lg" role="group" h="fit-content">
          <CardHeader py="2" pb="0">
            <Heading variant="h5">Airline & Flight Details</Heading>
          </CardHeader>
          <CardBody pb="0">
            <Stack>
              <Stack direction="row" align="center" spacing="6">
                <Image src={quote?.provider?.logo_uri} height="48px" width="48px" alt={quote?.provider?.name} />
                <Heading variant="h6">
                  {quote?.origin_airport_id}{' '}
                  <Box as="span" color="text.placeholder">
                    →
                  </Box>{' '}
                  {quote?.destination_airport_id}
                </Heading>
                <Text textStyle="bodySmallRegular">{stopCountLabel}</Text>
                <CopyButton text={buildFlightPathString(quote?.flight_path)} marginLeft="auto" />
              </Stack>
              <Text color="text.subtle" textStyle="captionSmallRegular">
                {quote?.provider?.name}
              </Text>
            </Stack>
            <Timeline data={flightData} />
          </CardBody>
        </Card>
        <PaperworkCard quote={quote} variant="fulfillment-partner" />
      </Stack>
      <Stack w="full" spacing="6" alignItems="flex-start">
        <Card h="fit-content">
          <CardBody p="4">
            <Stack spacing={6}>
              <Stack direction="row" align="center" justify="space-between" spacing="4" role="group">
                <Heading variant="h5">Cost Summary</Heading>
                <CopyLineItemsButton quote={quote} quoteVariant={bookedQuoteVariant} />
              </Stack>
              <QuoteVariantUI
                quote={quote}
                quoteVariant={bookedQuoteVariant}
                showCopy={false}
                showTotal={false}
                showMarkupBreakdown
              />
              <Validity quote={quote} />
            </Stack>
          </CardBody>
        </Card>
        <RequestDetailsCard request={quote?.request} quote={quote} showPaperwork={false} />
      </Stack>
    </Stack>
  );
};
